import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import App from './App';
import ErrorBoundary from './components/Common/ErrorBoundary';
import ThemeProvider from './contexts/ThemeProvider';
import DensityProvider from './contexts/DensityProvider';
import { initPendingQueue } from './services/pendingQueue';
import { reportFlush, startQueueToasts } from './services/queueToasts';
import AppToaster from './components/Common/AppToaster';
import { initServiceWorker } from './services/swUpdate';
import { drainQueuedWarnings } from './utils/warnings';
import { connectQueryClient } from './utils/refreshAfterWrite';
import './index.css';

// ---------------------------------------------------------------------------
// Sentry — error tracking + performance + session replay.
// Initialised before App so any boot-time error is captured.
// ---------------------------------------------------------------------------
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const SENTRY_ENABLED = !!SENTRY_DSN;
if (SENTRY_ENABLED) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: (import.meta.env.VITE_APP_ENV as string) || import.meta.env.MODE || 'development',
    release: (import.meta.env.VITE_APP_VERSION as string) || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Strip query strings that may carry tokens
    beforeSend(event) {
      try {
        if (event.request?.url) {
          event.request.url = event.request.url.split('?')[0];
        }
      } catch { /* noop */ }
      return event;
    },
  });
  // Errors typed into DevTools console don't fire window.onerror in modern
  // browsers, so expose an explicit smoke-test helper to verify the transport.
  (window as unknown as { __sentryTest?: (msg?: string) => string }).__sentryTest = (
    msg = 'Sentry frontend smoke test',
  ) => {
    Sentry.captureException(new Error(msg));
    return 'captured — check Sentry feed';
  };
}
// A status line, kept on purpose — so console.info, never console.log, which
// the noConsoleLog guard reserves for leftovers (Deep-test EXT-13).
// eslint-disable-next-line no-console
console.info(
  '[Sentry]',
  SENTRY_ENABLED
    ? `enabled (host=${(() => { try { return new URL(SENTRY_DSN!).host; } catch { return 'invalid-dsn'; } })()})`
    : 'disabled — VITE_SENTRY_DSN not set at build time',
);

// Sentry-aware error boundary that wraps our existing ErrorBoundary.
const SentryErrorBoundary = SENTRY_ENABLED
  ? Sentry.withErrorBoundary(ErrorBoundary, {
      fallback: ({ error, resetError }) => (
        <div role="alert" style={{ padding: '2rem', maxWidth: 600, margin: '4rem auto', fontFamily: 'sans-serif' }}>
          <h2 style={{ color: '#dc2626', marginBottom: '0.5rem' }}>Something went wrong</h2>
          <p style={{ color: '#57534e', marginBottom: '1rem' }}>
            We&apos;ve been notified and are looking into it. You can try again or reload the page.
          </p>
          <pre style={{ background: '#fafaf9', padding: '0.75rem', borderRadius: 8, fontSize: '0.75rem', color: '#44403c', overflow: 'auto', maxHeight: 200 }}>
            {error instanceof Error ? error.message : String(error)}
          </pre>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
            <button onClick={resetError} style={{ padding: '0.5rem 1rem', background: '#C2410C', color: 'white', border: 0, borderRadius: 8, cursor: 'pointer' }}>Try again</button>
            <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', background: 'white', color: '#44403c', border: '1px solid #d6d3d1', borderRadius: 8, cursor: 'pointer' }}>Reload page</button>
          </div>
        </div>
      ),
      showDialog: false,
    })
  : ErrorBoundary;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// New Bug Auto Refresh: every confirmed write re-reads what is on screen, for
// every page, with nothing for a page to remember. utils/refreshAfterWrite.ts.
connectQueryClient(queryClient);

// ---------------------------------------------------------------------------
// Captures made while offline.
//
// Started here rather than inside a page, because whoever queued the work is
// usually not the person who next opens the app, and never the page they
// happen to land on. It sends on app open, on reconnect, and once a minute
// while open; anything the server refuses outright is surfaced rather than
// dropped in silence.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// The offline shell.
//
// Registers the service worker and owns when a new deployment is allowed to
// take over — which is at an idle moment, never under a page somebody is
// typing into. services/swUpdate.ts has the why.
// ---------------------------------------------------------------------------
initServiceWorker();

// ---------------------------------------------------------------------------
// Warnings a page load interrupted.
//
// The <Toaster/> below sits beside <App/> and OUTSIDE the route tree, so an
// ordinary SPA navigation cannot unmount a toast — a warning raised by a
// handler that then navigates survives on its own. A full document load is the
// case that needs help: the two `window.location.href = '/login'` redirects in
// utils/api.ts, SettingsPage's logout, shellRecovery's reload. A warning
// raised just before one of those would be destroyed mid-sentence, and a
// warning nobody saw was not shown.
//
// Only warnings younger than a toast's own lifetime are replayed — see
// drainQueuedWarnings — so this restores what a load interrupted rather than
// resurfacing old news.
// ---------------------------------------------------------------------------
drainQueuedWarnings();

// The flush's toasts are held while the technician's PIN lock is up and told
// after the unlock (FM Technician review R2-2) — services/queueToasts.ts.
initPendingQueue(reportFlush);
startQueueToasts();


ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* Outside BrowserRouter: the theme is not route-dependent, and the
            class it sets belongs to <html>, above everything React renders. */}
        <ThemeProvider>
        <DensityProvider>
        <BrowserRouter>
          <App />
          <AppToaster />
        </BrowserRouter>
        </DensityProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  </React.StrictMode>,
);
